import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { formatDuration } from '../../utils/formatDuration';
import { formatEffectDuration } from '../../utils/effectDuration';
import { buildSurveyRows } from '../../utils/intelPanelData';
import { FreshnessCell } from './FreshnessCell';
import type { LatestIntelEntry } from '../../types/dossier';
import type { CrystalBallData, SpyOnMilitaryData, SurveyData, InfiltrateData } from '../../types/intel';

/**
 * SF-156 Component 5: four read-only Crystal Ball / Spy on Military / Survey / Infiltrate mobile
 * cards, new thin siblings to `MobileCrystalBallCard`/`MobileSpyOnMilitaryCard`/`MobileSurveyCard`
 * (see Component 5's Open Question resolution) — not literal reuses, since those components fetch
 * live via `useGatherIntel`/`useTargetIntel` and expect `IntelReportDto` shape, while Dossier's
 * data is already-fetched `LatestIntelEntry<T>`. Mirror the same visual language (colored header,
 * headline pair, grouped label/value rows) but source directly from the kingdom payload, and each
 * card independently renders its own "Gather {ReportType}" button when its own report is missing —
 * no cross-card priority ladder, since a detail view has no shared Actions cell to prioritize
 * within (unlike desktop's one-button-per-row Actions column).
 */

const CB_FG = '#b89fe0';
const SM_FG = '#8ec4a0';
const SV_FG = '#c4b078';
const INF_FG = '#e0923c';
const OFF_CLR = '#e8a04f';

const cardStyle: React.CSSProperties = {
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  overflow: 'hidden',
  marginBottom: 10,
};

const noDataText: React.CSSProperties = { color: COLORS.text.muted, fontSize: 12, margin: '0 0 8px' };

function CardHeader({
  label,
  fg,
  bg,
  entry,
  currentTick,
}: {
  label: string;
  fg: string;
  bg: string;
  entry: LatestIntelEntry<unknown> | null;
  currentTick: number;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: bg, borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ color: fg, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>
      {entry && <FreshnessCell capturedAtTick={entry.capturedAtTick} currentTick={currentTick} />}
    </div>
  );
}

function GatherButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ width: '100%', minHeight: 44, borderRadius: 6, border: 'none', background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
    >
      {label}
    </button>
  );
}

function Headline({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ flex: 1, background: COLORS.cardAlt, borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ color: COLORS.text.tertiary, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color, fontSize: 17, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function GroupLabel({ text }: { text: string }) {
  return (
    <div style={{ color: COLORS.text.tertiary, fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '8px 0 3px', paddingTop: 6, borderTop: `1px solid ${COLORS.border}` }}>
      {text}
    </div>
  );
}

function LV({ label, value, color, muted }: { label: string; value: string; color?: string; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1.5px 0', fontSize: 12 }}>
      <span style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span style={{ color: muted ? COLORS.text.muted : (color ?? COLORS.text.primary), fontStyle: muted ? 'italic' : 'normal' }}>{value}</span>
    </div>
  );
}

interface CardProps<T> {
  entry: LatestIntelEntry<T> | null;
  currentTick: number;
  onGather: () => void;
}

export function MobileDossierCbCard({ entry, currentTick, onGather }: CardProps<CrystalBallData>) {
  return (
    <div style={cardStyle}>
      <CardHeader label="Crystal Ball" fg={CB_FG} bg="rgba(107,79,160,0.15)" entry={entry} currentTick={currentTick} />
      <div style={{ padding: 12 }}>
        {!entry ? (
          <>
            <p style={noDataText}>Reveals resources, military units, and offense/defense points.</p>
            <GatherButton label="Gather Crystal Ball" onClick={onGather} />
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <Headline label="Offense" value={formatNumber(entry.data.offensePoints)} color={OFF_CLR} />
              <Headline label="Defense" value={formatNumber(entry.data.defensePoints)} color={COLORS.link} />
            </div>

            <GroupLabel text="Resources" />
            <LV label="Gold (gc)" value={formatNumber(entry.data.money)} color={COLORS.gold} />
            <LV label="Food (bushels)" value={formatNumber(entry.data.food)} />
            <LV label="Runes" value={formatNumber(entry.data.runes)} color={CB_FG} />
            <LV
              label="Trade Balance (gc)"
              value={`${entry.data.tradeBalance >= 0 ? '+' : ''}${formatNumber(entry.data.tradeBalance)}`}
              color={entry.data.tradeBalance >= 0 ? COLORS.positive : COLORS.negative}
            />

            <GroupLabel text="Military Units" />
            <LV label="Peasants" value={formatNumber(entry.data.peasants)} />
            <LV label="Soldiers" value={formatNumber(entry.data.soldiers)} />
            <LV label="Offense Specialists" value={formatNumber(entry.data.offenseSpecialists)} />
            <LV label="Defense Specialists" value={formatNumber(entry.data.defenseSpecialists)} />
            <LV label="Elites" value={formatNumber(entry.data.elites)} />
            <LV label="War Horses" value={formatNumber(entry.data.warHorses)} />
            <LV label="Prisoners" value={formatNumber(entry.data.prisoners)} />
            <LV label="Building Efficiency (%)" value={`${Math.round(entry.data.buildingEfficiency * 100)}`} />

            {entry.data.activeEffects.length > 0 && (
              <>
                <GroupLabel text="Effects" />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                  {entry.data.activeEffects.map((e, i) => (
                    <span
                      key={`${e.effectType}-${i}`}
                      style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 3, fontSize: 10, background: 'rgba(107,79,160,0.2)', color: '#c4b0e8', border: '1px solid rgba(107,79,160,0.3)' }}
                    >
                      {formatEnumLabel(e.effectType)} ({formatEffectDuration(e)})
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function MobileDossierSomCard({ entry, currentTick, onGather }: CardProps<SpyOnMilitaryData>) {
  return (
    <div style={cardStyle}>
      <CardHeader label="Spy on Military" fg={SM_FG} bg="rgba(74,122,90,0.15)" entry={entry} currentTick={currentTick} />
      <div style={{ padding: 12 }}>
        {!entry ? (
          <>
            <p style={noDataText}>Reveals modified offense/defense and army status.</p>
            <GatherButton label="Gather Spy on Military" onClick={onGather} />
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <Headline label="Net Offense" value={entry.data.offensePoints != null ? formatNumber(entry.data.offensePoints) : '—'} color={OFF_CLR} />
              <Headline label="Net Defense" value={entry.data.defensePoints != null ? formatNumber(entry.data.defensePoints) : '—'} color={COLORS.link} />
            </div>

            {entry.data.offensiveME != null && <LV label="Off. ME (%)" value={`${(entry.data.offensiveME * 100).toFixed(0)}`} color={COLORS.positive} />}
            <LV label="Def. ME (%)" value={`${(entry.data.defensiveME * 100).toFixed(0)}`} />
            <LV label="Generals" value={entry.data.generals != null ? formatNumber(entry.data.generals) : '—'} />

            <GroupLabel text="Army Status" />
            <LV label="Army" value={entry.data.hasArmiesOut ? 'Out' : 'Home'} color={entry.data.hasArmiesOut ? COLORS.warning : COLORS.positive} />
            {entry.data.hasArmiesOut && entry.data.nextReturnTicks != null && (
              <LV label="Returns In" value={formatDuration(entry.data.nextReturnTicks)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function MobileDossierSurveyCard({ entry, currentTick, onGather }: CardProps<SurveyData>) {
  const rows = entry ? buildSurveyRows(entry.data.buildings, entry.data.totalLand) : [];
  return (
    <div style={cardStyle}>
      <CardHeader label="Survey" fg={SV_FG} bg="rgba(122,106,64,0.15)" entry={entry} currentTick={currentTick} />
      <div style={{ padding: 12 }}>
        {!entry ? (
          <>
            <p style={noDataText}>Reveals the target's building breakdown and construction queue.</p>
            <GatherButton label="Gather Survey" onClick={onGather} />
          </>
        ) : (
          <>
            <p style={{ color: COLORS.text.muted, fontSize: 11, margin: '0 0 10px', fontStyle: 'italic' }}>
              Informational only — doesn't affect your attack outcome.
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: COLORS.text.tertiary, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Buildings</span>
              <span style={{ color: COLORS.text.muted, fontSize: 11 }}>{formatNumber(entry.data.totalLand)} acres</span>
            </div>
            {rows.map((b) => (
              <div key={b.key} style={{ display: 'flex', padding: '2px 0' }}>
                <span style={{ color: COLORS.text.secondary, fontSize: 12, flex: 1 }}>{b.label}</span>
                <span style={{ color: COLORS.text.primary, fontSize: 12, width: 42, textAlign: 'right' }}>{formatNumber(b.count)}</span>
                <span style={{ color: COLORS.text.muted, fontSize: 12, width: 52, textAlign: 'right' }}>({b.pct}%)</span>
              </div>
            ))}
            {entry.data.construction.length > 0 && (
              <>
                <GroupLabel text="Under Construction" />
                {entry.data.construction.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span style={{ color: COLORS.text.secondary, fontSize: 12 }}>{formatEnumLabel(c.buildingType)}</span>
                    <span style={{ color: COLORS.text.primary, fontSize: 12 }}>{formatNumber(c.acres)} acres · {formatDuration(c.ticksRemaining)}</span>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function MobileDossierInfiltrateCard({ entry, currentTick, onGather }: CardProps<InfiltrateData>) {
  return (
    <div style={cardStyle}>
      <CardHeader label="Infiltrate" fg={INF_FG} bg="rgba(224,146,60,0.14)" entry={entry} currentTick={currentTick} />
      <div style={{ padding: 12 }}>
        {!entry ? (
          <>
            <p style={noDataText}>Reveals raw thieves-per-acre for op planning.</p>
            <GatherButton label="Gather Infiltrate" onClick={onGather} />
          </>
        ) : (
          <>
            <LV label="Thieves" value={formatNumber(entry.data.thieves)} />
            <LV label="Raw TPA" value={entry.data.rawTPA.toFixed(2)} color={INF_FG} />
          </>
        )}
      </div>
    </div>
  );
}
